// The kid-facing explanation.
//
// Three registers, because a five-year-old and a fifteen-year-old need
// genuinely different things, not the same text at different sizes.
//
//   Band A     short words and icons, addressed to the child. Band is a math
//              level, NOT a reading level, and conflating the two was a real
//              mistake here: a five-year-old on Band A math may read fluently.
//              So this speaks to the kid and merely offers a grown-up, rather
//              than assuming one is required.
//   Bands B-C  short sentences, concrete nouns, no abstractions.
//   Bands D-E  treated as someone who can be told how the system works and
//              why, including the parts adults usually hide from teenagers.
import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";
import {ENGAGEMENT_CONFIG} from '../shared/engagement/config';
import {stampRules} from '../shared/engagement/rewards';
import type { Band } from "../shared/economy";

interface Entry { icon: string; title: string; body: string }

export function entriesFor(band: Band, currency: string): Entry[] {
  if (band === "A") {
    return [
      { icon: "📚", title: "Explore your learning", body: "Try your math puzzles. Ask for help whenever you need it." },
      { icon: "🏔️", title: "Climb the mountain", body: "Every day you practice, your climber goes higher. It never slides back down, ever." },
      { icon: "⭐", title: "Collect stars", body: ENGAGEMENT_CONFIG.stamps.startHere },
      { icon: "🚩", title: "Plant a flag", body: "Reach the top of a mountain and the badge is yours to keep." },
      { icon: "🔒", title: "Nobody can take it", body: `Your ${currency} and your climb belong to you. Nobody can take them away.` },
    ];
  }

  if (band === "B" || band === "C") {
    return [
      { icon: "🎯", title: "Hit your target", body: "Do your math, then log it here. That is the whole job for the day." },
      { icon: "🎉", title: "The rest of the day is yours", body: "Once you hit it, you have summited. Free time, no arguing about it." },
      { icon: "🪙", title: `You earn ${currency}`, body: stampRules(currency) },
      { icon: "🎁", title: "Spend it on prizes", body: "Pick something to save up for. Once you have enough, ask for it. A parent approves and delivers it. Your bucks are spent when it arrives." },
      { icon: "🏔️", title: "Climb all year", body: "Every day adds to your climb. Table Rock first, then Mount Mitchell, all the way to Everest by the end of the year." },
      { icon: "🔒", title: "Nobody can take it away", body: `Your ${currency} and your climb are yours. Not even a grown-up can take them away as a punishment.` },
      { icon: "💰", title: "Saving grows it", body: "Put some away, leave it 30 days, get extra. Change your mind and you get all of it back anyway." },
      { icon: "🙋", title: "Ask for what you want", body: "Tap Wish any time. You can also ask for prizes you would rather have, or ask to swap out one you do not like." },
    ];
  }

  return [
    { icon: "🎯", title: "Effort is what pays, not scores", body: `Learning is the biggest reward. ${currency} celebrate real attempts and consistency. Mastery brings a celebration.` },
    { icon: "🎉", title: "Hit the target, own your time", body: "The daily target is the deal. Clear it and the rest of the day is yours with nobody checking in." },
    { icon: "🪙", title: `${currency}, and how it works`, body: stampRules(currency) },
    { icon: "🔒", title: "Your balance cannot be docked", body: "There is no feature that lets anyone take your balance away as a punishment. That was a deliberate decision when this was built, not an oversight." },
    { icon: "🏔️", title: "The year-long climb", body: "Nothing to do with money. It counts days, so showing up is what moves it. Everest is about 129 climbing days, which works out to 3 or 4 days a week all year." },
    { icon: "💰", title: "The vault is a real lesson", body: "Leave it 30 days and it grows 10%. Pull it out early and you get exactly what you put in. Most real accounts charge you for leaving early. This one never does." },
    { icon: "🗣️", title: "You get a say", body: "Look through everything for your grade and ask for what you actually want. If something on your shelf is not for you, say so and ask to swap it." },
  ];
}

export function KidGuide({ band, currency, look='adventure' }: { band: Band; currency: string; look?:'adventure'|'studio' }) {
  const [open, setOpen] = useState(false);
  const entries = entriesFor(look==='studio'?'D':band, currency);
  const title = band === "A" ? "How this works" : band === "B" || band === "C" ? "How this works" : "How this actually works";

  return (
    <section className={`kid-guide ${open ? "open" : ""}`}>
      <button className="kid-guide-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span>{look!=='studio'&&'❓ '}{title}</span>
        <CaretDown weight="bold" className="kid-guide-caret" />
      </button>

      {open && (
        <div className="kid-guide-body">
          {band === "A" && (
            <p className="kid-guide-grownup">
              👋 Read this on your own, or with a grown-up. Whichever you like.
            </p>
          )}
          <ul>
            {entries.map((entry) => (
              <li key={entry.title}>
                {look!=='studio'&&<span className="kid-guide-icon" aria-hidden="true">{entry.icon}</span>}
                <div>
                  <strong>{entry.title}</strong>
                  <span>{entry.body}</span>
                </div>
              </li>
            ))}
          </ul>
          {(
            <p className="kid-guide-foot">
              If something in here feels unfair, say so. It is supposed to be worth doing, not
              something being done to you.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
